import {
  BehaviorSubject,
  forkJoin as observableForkJoin,
  Observable,
  of as observableOf,
  of,
  Subject,
} from 'rxjs';

import {
  catchError,
  delay,
  distinctUntilChanged,
  first,
  map,
  mergeMap,
  shareReplay,
} from 'rxjs/operators';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {ViewConfig} from './viewconfig';
import {CacheService} from '../../cache/cache.service';
import {DetailService} from './detail.service';
import {Resource, ResourceAttributeWithPH, ResourceWithPermissionHints} from '../resource';
import {ResourceService} from '../resource.service';
import {DialogConfiguration} from '../../cache/uiconfiguration/dialogconfiguration';
import {ResourceDetailAbstractComponent, ValidationInfo} from './detail-abstract.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DialogsService} from '../../shared/dialog/dialogs.service';
import {LanguageService} from '../../language/language.service';
import {ViewTabComponent} from './viewtab.component';
import {ErrorService} from '../../error/error.service';
import {WizardConfigurationNotFoundError} from '../../error/errordefinitions';
import {DisplayAttribute} from '../displayattribute';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent} from '@angular/cdk/stepper';
import {MatStep, MatStepper} from '@angular/material/stepper';
import {CandeactivateDialogComponent} from './controls/canDeactivateGuard/candeactivate-dialog.component';
import {getResourceLabel} from '../resource-label';

@Component({
  selector: 'resource-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {displayDefaultIndicatorType: false},
    },
  ],
})
export class ResourceWizardComponent
  extends ResourceDetailAbstractComponent
  implements AfterViewChecked
{
  getCaughtObservable(obs: Observable<any>, fatal: boolean): Observable<any> {
    return super.getCaughtObservable(obs, fatal);
  }

  ngAfterViewChecked() {
    if (this.saveAllTabsOnInit) this.saveAllTabs();
    //cd for super fast create dialogs
    this.onResize();
  }
  WizardType = WizardType;
  saveAllTabsOnInit = false;
  wizardType: WizardType = WizardType.None;
  urlId: string = null;
  objectIds?: string | string[] = null;

  @ViewChild('stepper') stepperComponent: MatStepper;
  @ViewChildren(ViewTabComponent) viewTabs: QueryList<ViewTabComponent>;

  loading: boolean = true;

  editEnabled: boolean = true;
  historyTitleSubject: Subject<string>;

  // BatchList wizard: the selected objects are edited one after another in this instance
  batchListIds: string[] = null;
  batchListIndex: number = 0;
  batchListTotal: number = 0;
  batchListSavedCount: number = 0;
  /** ['2', '5'] for the "Object {0} of {1}" caption; stored so OnPush sees a stable array */
  batchListProgressArgs: string[] = [];
  /** label of the object currently shown (DisplayName or "⟨ No ... ⟩") */
  batchListLabel: Observable<string> = null;
  private _resLocaleObs: Observable<string> = null;
  private _dgConfObs: Observable<DialogConfiguration> = null;
  // incremented per object load so that a late response of a previous load is ignored
  private _loadToken: number = 0;

  /** normalises the object ids of a wizard call (scalar, comma separated or array) to an array */
  public static normalizeObjectIds(ids: string | string[] | null | undefined): string[] {
    if (ids == null) return [];
    const arr: string[] = Array.isArray(ids) ? ids : ids.split(',');
    return arr.map(id => (id == null ? '' : ('' + id).trim())).filter(id => id.length > 0);
  }

  /** index of the next object of a batch list, -1 when the list is exhausted */
  public static nextBatchListIndex(index: number, total: number): number {
    return index + 1 < total ? index + 1 : -1;
  }

  /**
   * Whether the result of save() means the current object is done (saved, approval pending or
   * nothing changed). null means the user has to stay on the object (validation error, summary
   * cancelled or a failed request).
   */
  public static isSubmitCompleted(oks: boolean[] | boolean | null | undefined): boolean {
    return oks != null;
  }

  public static progressArgs(index: number, total: number): string[] {
    return ['' + (index + 1), '' + total];
  }

  public static formatBatchListTitle(label: string, position: number, total: number): string {
    const suffix = '(' + position + '/' + total + ')';
    if (label == null || label.trim().length === 0) return suffix;
    return label + ' ' + suffix;
  }

  constructor(
    changeDetector: ChangeDetectorRef,
    cacheService: CacheService,
    private detailService: DetailService,
    resourceService: ResourceService,
    snackBar: MatSnackBar,
    dialog: MatDialog,
    dialogsService: DialogsService,
    elementRef: ElementRef,
    errorService: ErrorService,
    protected envConfig: EnvironmentConfigService,
  ) {
    super(
      cacheService,
      snackBar,
      changeDetector,
      dialog,
      dialogsService,
      resourceService,
      elementRef,
      errorService,
      envConfig,
    );
  }

  public init(
    wizardType: WizardType,
    urlId: string,
    historyTitleSubject?: Subject<string>,
    objectIds?: string | string[],
  ) {
    this.viewConfig = null;
    this.resource = null;
    this.loading = true;
    this.resetUI();
    this.wizardType = wizardType;
    this.stepperComponent?.reset();

    // Wizards are always editable, we have to make sure that edit is true, because save is setting it to false
    this.editEnabled = true;

    this.changeDetector.markForCheck();

    this.urlId = urlId;
    if (objectIds != null && !Array.isArray(objectIds) && objectIds.indexOf(',') !== -1) {
      objectIds = objectIds.split(',');
    }
    this.objectIds = objectIds;
    this.isCreate = false;
    this.isBatch = false;

    this.historyTitleSubject = historyTitleSubject;

    let resLocaleObs = this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
    let dgConfObs = this.cacheService.getDialogConfigurationByURLID(
      urlId,
      wizardType === WizardType.Create,
    );
    if (dgConfObs == null) {
      this.handleFatalError(new WizardConfigurationNotFoundError(urlId));
      return;
    }

    dgConfObs = dgConfObs.pipe(
      catchError((error, caught) => this.handleFatalError(error, caught)),
      distinctUntilChanged(),
      shareReplay(1),
    );

    if (wizardType === WizardType.Create) {
      dgConfObs.pipe(first()).subscribe((dgConf: DialogConfiguration) => {
        if (dgConf == null) {
          this.allowAdvancedDetail.pipe(first()).subscribe(allow => {
            if (allow) {
              this.initCreateWizard(dgConf, dgConfObs, resLocaleObs);
            } else {
              this.handleFatalError(new WizardConfigurationNotFoundError(urlId));
            }
          });
        } else {
          this.initCreateWizard(dgConf, dgConfObs, resLocaleObs);
        }
      });
    } else if (
      ((wizardType === WizardType.Single || wizardType === WizardType.Approval) &&
        typeof this.objectIds === 'string') ||
      wizardType === WizardType.Multiple ||
      wizardType === WizardType.ApprovalMultiple
    ) {
      this.isCreate = false;
      if (typeof this.objectIds === 'string') {
        this.isBatch = false;
        this.batchIds = [this.objectIds];
      } else {
        this.isBatch = true;
        this.batchIds = <string[]>this.objectIds;
      }

      this.loadObject((<string[]>this.batchIds)[0], resLocaleObs, dgConfObs);
    } else if (wizardType === WizardType.BatchList) {
      // every selected object is edited on its own, one after another (see advanceBatchList)
      this.isCreate = false;
      this.isBatch = false;
      this.batchListIds = ResourceWizardComponent.normalizeObjectIds(this.objectIds);
      this.batchListTotal = this.batchListIds.length;
      this.batchListIndex = 0;
      this.batchListSavedCount = 0;
      this._resLocaleObs = resLocaleObs;
      this._dgConfObs = dgConfObs;

      if (this.batchListTotal === 0) {
        this.onCloseView.emit({RefreshActive: false});
        this.resetComponent();
        return;
      }

      this.loadObject(this.batchListIds[0], resLocaleObs, dgConfObs);
    } else {
      alert('Wizard type not implemented yet.');
    }
  }

  /**
   * Loads one existing object into the wizard: fetches it, takes the save copy, builds the view
   * configuration and pushes the title to the history. Used by the edit, batch, approval and
   * batch list wizards.
   */
  private loadObject(
    id: string,
    resLocaleObs: Observable<string>,
    dgConfObs: Observable<DialogConfiguration>,
  ): void {
    const isBatchList = this.wizardType === WizardType.BatchList;
    if (isBatchList) {
      this.batchIds = [id];
      this.batchListProgressArgs = ResourceWizardComponent.progressArgs(
        this.batchListIndex,
        this.batchListTotal,
      );
    }
    const token = ++this._loadToken;

    this.resourceService
      .getResourceWithPermissionHints(id)
      .pipe(catchError((error, caught) => this.handleFatalError(error, caught)))
      .subscribe((res: ResourceWithPermissionHints) => {
        // a newer object has been requested in the meantime (skip / cancel / new wizard)
        if (token !== this._loadToken) return;
        if (res == null) return;

        this.resource = new BehaviorSubject(res);

        this.saveCopy = new Resource();
        Object.keys(res).forEach((key: string) => {
          if (Array.isArray(res[key].Values)) {
            this.saveCopy[key] = res[key].Values.slice();
          } else {
            this.saveCopy[key] = res[key].Values;
          }
        });

        this.viewConfig = this.detailService
          .getBodyTabConfig(
            this.resource.asObservable().pipe(shareReplay(1)),
            resLocaleObs,
            dgConfObs,
            this.isBatch,
          )
          .pipe(
            distinctUntilChanged(),
            shareReplay(1),
            catchError((error, caught) => this.handleFatalError(error, caught)),
          );

        if (isBatchList) {
          this.batchListLabel = this.resource.pipe(
            mergeMap((r: ResourceWithPermissionHints) =>
              getResourceLabel(r, this.cacheService, this.envConfig),
            ),
            distinctUntilChanged(),
            shareReplay(1),
          );
        }

        this.loading = false;

        this.changeDetector.markForCheck();
        this.onResize();

        if (isBatchList) {
          const position = this.batchListIndex + 1;
          const total = this.batchListTotal;
          this.batchListLabel
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((label: string) => {
              if (token !== this._loadToken) return;
              this.historyTitleSubject?.next(
                ResourceWizardComponent.formatBatchListTitle(label, position, total),
              );
              this.changeDetector.markForCheck();
              this.onResize();
            });
          return;
        }

        this.viewConfig
          .pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
          )
          .subscribe((btc: ViewConfig) => {
            if (btc != null && btc.DisplayName != null) {
              btc.DisplayName.pipe(
                catchError((error, caught) => this.handleError(error, caught)),
                first(),
              ).subscribe((displayName: string) => {
                this.historyTitleSubject.next(displayName);
                this.changeDetector.markForCheck();
                this.onResize();
              });
            }
          });
      });
  }

  private initCreateWizard(
    dgConf: DialogConfiguration,
    dgConfObs: Observable<DialogConfiguration>,
    resLocaleObs: Observable<string>,
  ) {
    this.isCreate = true;

    // create empty resource body
    const objType = dgConf == null ? this.urlId : dgConf.objecttype;
    this.cacheService
      .getDisplayAttributesForResource(objType)
      .subscribe((atts: DisplayAttribute[]) => {
        if (atts.length === 0) {
          this.handleFatalError(new WizardConfigurationNotFoundError(this.urlId));
        }

        let newResource = new ResourceWithPermissionHints();

        atts.forEach((att: DisplayAttribute) => {
          if (att.Name === this.envConfig.getAttributeName('ObjectType')) {
            newResource[att.Name] = <ResourceAttributeWithPH<string>>{
              PermissionHint: ['Add', 'Create', 'Modify', 'Read', 'Remove'],
              Values: objType,
            };
          } else {
            newResource[att.Name] = <ResourceAttributeWithPH<any>>{
              PermissionHint: ['Add', 'Create', 'Modify', 'Read', 'Remove'],
              Values: null,
            };
          }
        });
        this.saveCopy = null;
        this.resource = new BehaviorSubject(newResource);
        this.viewConfig = this.detailService
          .getBodyTabConfig(
            this.resource.asObservable().pipe(shareReplay(1)),
            resLocaleObs,
            dgConfObs,
            true,
          )
          .pipe(
            distinctUntilChanged(),
            shareReplay(1),
            catchError(error => {
              this.cacheService
                .getLanguageResource()
                .pipe(first())
                .subscribe(lr => {
                  const title = LanguageService.translate(
                    'ResourceDetailErrorParsingTitle',
                    'Error Parsing Dialog Configuration',
                    lr,
                  );
                  const msg = LanguageService.translate(
                    'ResourceDetailErrorParsingMessage',
                    'There occurred an error while parsing the dialog configuration',
                    lr,
                  );
                  setTimeout(() => {
                    this.dialogsService.webserviceError(error, title, msg, lr).subscribe(
                      () => {
                        //this.afterDelete.emit(true);
                      },
                      () => {},
                    );
                  });
                });

              console.error(error);
              return observableOf(null);
            }),
          );
        this.loading = false;
        this.changeDetector.markForCheck();
        this.onResize();

        this.viewConfig.pipe(first()).subscribe((btc: ViewConfig) => {
          if (btc != null && btc.DisplayName != null) {
            btc.DisplayName.pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            ).subscribe((displayName: string) => {
              this.historyTitleSubject.next(displayName);
            });
          }
        });
        this.saveAllTabs();
      });
  }

  goToNextStep(): void {
    if (this.stepperComponent == null) return;
    if (this.viewTabs == null) return;
    this.getValidationErrorCount(true, this.stepperComponent.selectedIndex)
      .pipe(catchError((error, caught) => this.handleError(error, caught)))
      .subscribe((errors: number) => {
        if (errors === 0) {
          this.stepperComponent.next();
          this.changeDetector.markForCheck();
        } else {
          this.cacheService
            .getLanguageResource()
            .pipe(first())
            .subscribe(strRes => {
              let msg: string;
              if (errors === 1) {
                msg = LanguageService.translate(
                  'DetailValidationErrorsSingle',
                  'You have an error. Please fix it.',
                  strRes,
                );
              } else {
                msg = LanguageService.translate(
                  'DetailValidationErrors',
                  'You have {0} errors. Please fix them.',
                  strRes,
                  ['' + errors],
                );
              }
              this.snackBar.open(msg, undefined, {duration: 5000});
            });
        }
      });
  }

  stepEntered(event: StepperSelectionEvent): void {
    if (event?.selectedIndex >= 0) {
      // stepper.reset() also fires selectionChange while no tabs are rendered
      this.viewTabs?.toArray()[event.selectedIndex]?.refreshUI();
    }
  }

  public getValidationErrorCount(
    includeNonTouched: boolean,
    step: number = -1,
  ): Observable<number> {
    if (this.viewTabs == null) return observableOf(0);

    //Reset all wizard steps
    this.stepperComponent.steps.forEach(step => (step.hasError = false));

    //get all tabs till active tab is reached
    let tabObs: Observable<ValidationInfo>[] = [];
    this.viewTabs.forEach((tab, index) => {
      if (index <= step || step == -1) {
        tabObs.push(
          tab
            .getErrorCount(includeNonTouched)
            .pipe(map(number => new ValidationInfo(index, number))),
        );
      }
    });

    //turn on error message
    return observableForkJoin(tabObs).pipe(
      map((valids: ValidationInfo[]) => {
        let errors: number = 0;
        valids.forEach((valid: ValidationInfo) => {
          if (valid.Errors > 0) {
            this.stepperComponent.steps.get(valid.TabIndex).hasError = true;
            errors += valid.Errors;
          }
        });
        return errors;
      }),
    );
  }

  protected saveAllTabs(): void {
    if (this.viewTabs == null || this.viewTabs.length === 0) {
      this.saveAllTabsOnInit = true;
    } else {
      this.saveAllTabsOnInit = false;
      this.viewTabs.forEach(vt => {
        vt.saveAllControlValues();
      });
    }
  }

  protected resetUI(): void {
    if (!(this.viewTabs == null || this.viewTabs.length === 0)) {
      this.viewTabs.forEach(vt => {
        vt.resetUI();
      });
    }
  }

  get allowTimeline(): Observable<boolean> {
    // allow Timeline button only if the wizard is an edit wizard (therefore the underlying object
    // exists) and the user can read the Timeline attribute of the underlying object
    if (this.wizardType !== WizardType.Single && this.wizardType !== WizardType.BatchList) {
      return of(false);
    }
    return super.allowTimeline;
  }

  get showWizard(): boolean {
    return this.wizardType !== WizardType.None;
  }

  get isBatchList(): boolean {
    return this.wizardType === WizardType.BatchList;
  }

  submit(): void {
    this.saveAllTabs();
    const obs = this.save();
    if (obs == null) return;
    // added small delay just to be save on slower systems so that objects have the chance to disappear in lists
    obs.pipe(delay(500)).subscribe(
      (oks: boolean[]) => {
        if (!ResourceWizardComponent.isSubmitCompleted(oks)) return;
        if (this.wizardType === WizardType.BatchList) {
          this.batchListSavedCount++;
          this.advanceBatchList();
        } else {
          this.onCloseView.emit({RefreshActive: true});
          this.resetComponent();
        }
      },
      error2 => {
        // TODO handle error?
        console.log(error2);
      },
    );
  }

  /** Batch list: shows the next selected object or closes the wizard after the last one. */
  private advanceBatchList(): void {
    const next = ResourceWizardComponent.nextBatchListIndex(
      this.batchListIndex,
      this.batchListTotal,
    );
    if (next < 0) {
      this.onCloseView.emit({RefreshActive: this.batchListSavedCount > 0});
      this.resetComponent();
      return;
    }
    this.batchListIndex = next;
    this.prepareForNextObject();
    this.loadObject(this.batchListIds[next], this._resLocaleObs, this._dgConfObs);
  }

  /** Clears everything the previous object left behind before the next one is loaded. */
  private prepareForNextObject(): void {
    // reset while the old steps still exist, otherwise the stepper keeps a stale selectedIndex
    this.stepperComponent?.reset();
    this.resetUI();
    this.viewConfig = null;
    this.resource = null;
    this.batchListLabel = null;
    this.loading = true;
    // save() disables editing and the "nothing changed" / "approval pending" paths never re-enable it
    this.editEnabled = true;
    this.waitSpinner = false;
    this.freshResource = true;
    // a saved locale change leaves a null entry which the next save() cannot iterate
    this.resourceLocaleChange = {};
    this.saveCopy = null;
    // the locale picker is recreated empty, so the locale bindings must start empty as well
    this.resLocale.next('');
    this.advancedDetail = false;
    this.changeDetector.markForCheck();
  }

  /** Batch list: skips the current object without saving it. */
  skip(): void {
    if (this.wizardType !== WizardType.BatchList) return;
    if (this.loading || this.waitSpinner) return;
    this.confirmDiscard()
      .pipe(first())
      .subscribe((discard: boolean) => {
        if (discard) this.advanceBatchList();
      });
  }

  /** true when the current object has no unsaved changes or the user agreed to discard them */
  private confirmDiscard(): Observable<boolean> {
    if (this.freshResource) return observableOf(true);
    return this.dialog
      .open(CandeactivateDialogComponent)
      .afterClosed()
      .pipe(map((leave: boolean) => leave === true));
  }

  protected onApprovalPendingSaved(): void {
    // the batch list advances from submit(); closing here would end the whole list
    if (this.wizardType === WizardType.BatchList) return;
    super.onApprovalPendingSaved();
  }

  approveSubmit(type: any): void {
    this.saveAllTabs();
    const obs = this.approve(type);
    if (obs == null) return;
    // small delay because approve/reject takes a bit longer and therefore approval would otherwise not disappear in list
    obs.pipe(delay(500)).subscribe(
      (oks: boolean[]) => {
        if (oks != null) {
          this.onCloseView.emit({RefreshActive: true});
          this.resetComponent();
        }
      },
      error2 => {
        // TODO handle error?
        console.log(error2);
      },
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (!this.freshResource) {
      $event.returnValue = false;
    }
  }

  cancel(): void {
    if (this.wizardType === WizardType.BatchList) {
      this.confirmDiscard()
        .pipe(first())
        .subscribe((discard: boolean) => {
          if (!discard) return;
          this.onCloseView.emit({RefreshActive: this.batchListSavedCount > 0});
          this.resetComponent();
        });
      return;
    }
    this.onCloseView.emit({RefreshActive: false});
    this.resetComponent();
  }

  protected resetComponent(): void {
    this._loadToken++;
    this.resource = null;
    this.wizardType = WizardType.None;
    this.freshResource = true;
    this.resourceLocaleChange = {};
    this.batchListIds = null;
    this.batchListIndex = 0;
    this.batchListTotal = 0;
    this.batchListSavedCount = 0;
    this.batchListProgressArgs = [];
    this.batchListLabel = null;
    this.stepperComponent?.reset();
  }

  toggleAdvancedDetail(): void {
    super.toggleAdvancedDetail();
    this.loading = true;
    this.changeDetector.markForCheck();

    if (this.advancedDetail) {
      if (
        this.wizardType === WizardType.Create ||
        this.wizardType === WizardType.Single ||
        this.wizardType === WizardType.Approval ||
        this.wizardType === WizardType.BatchList
      ) {
        // Added distinctUntilChanged and shareReplay to resourceType to prevent reloading of whole wizard after every value change
        this.viewConfig = this.detailService
          .getAdvancedBodyTabConfigOfResourceType(
            this.resource.pipe(
              map(r => r[this.envConfig.getAttributeName('ObjectType')].Values),
              distinctUntilChanged(),
              shareReplay(1),
            ),
            this.resource.asObservable().pipe(shareReplay(1)),
            this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1)),
          )
          .pipe(
            distinctUntilChanged(),
            shareReplay(1),
            catchError((error, caught) => this.handleFatalError(error, caught)),
          );
      }
    } else {
      let resLocaleObs = this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
      let dgConfObs = this.cacheService.getDialogConfigurationByURLID(
        this.urlId,
        this.wizardType === WizardType.Create,
      );
      if (dgConfObs == null) {
        this.handleFatalError(new WizardConfigurationNotFoundError(this.urlId));
        return;
      }

      dgConfObs = dgConfObs.pipe(
        catchError((error, caught) => this.handleFatalError(error, caught)),
        distinctUntilChanged(),
        shareReplay(1),
      );

      if (
        this.wizardType === WizardType.Create ||
        this.wizardType === WizardType.Single ||
        this.wizardType === WizardType.Approval ||
        this.wizardType === WizardType.BatchList
      ) {
        this.viewConfig = this.detailService
          .getBodyTabConfig(
            this.resource.asObservable().pipe(shareReplay(1)),
            resLocaleObs,
            dgConfObs,
          )
          .pipe(
            distinctUntilChanged(),
            shareReplay(1),
            catchError((error, caught) => this.handleFatalError(error, caught)),
          );
      }
    }

    if (this.viewConfig != null) {
      this.viewConfig.pipe(first()).subscribe((btc: ViewConfig) => {
        this.loading = false;
        this.changeDetector.markForCheck();
        this.onResize();

        // the batch list keeps its "name (n/total)" title
        if (btc != null && btc.DisplayName != null && this.wizardType !== WizardType.BatchList) {
          btc.DisplayName.pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
          ).subscribe((displayName: string) => {
            this.historyTitleSubject.next(displayName);
          });
        }
      });
    }
  }
}

export enum WizardType {
  None = 0,
  Create = 1,
  Single,
  Multiple,
  Approval,
  ApprovalMultiple,
  BatchList,
}
